/**
 * 字典缓存模块（单例）
 * 无需 Provider 包裹，任意组件直接 import 使用
 *
 * 使用方式：
 *   const { items, options, loading } = useDictItems('asset_status');
 *   const label = useDictLabel('asset_status', 'active'); // → "在用"
 *   clearDictCache('asset_status'); // 字典管理页保存后调用
 */

import { useState, useEffect } from "react";
import { getDictItemsByType } from "./api/dict";

// ─────────────────────────────────────────────
// 单例缓存（模块级变量，全应用唯一）
// ─────────────────────────────────────────────
const memCache = {}; // { [type]: DictItem[] }
const pendingRequests = {}; // { [type]: Promise }  防并发重复请求

// ─────────────────────────────────────────────
// localStorage 工具
// ─────────────────────────────────────────────
const LS_PREFIX = "dict:";

function readFromStorage(type) {
  try {
    const raw = localStorage.getItem(`${LS_PREFIX}${type}`);
    if (!raw) return null;
    const { items, expiredDate } = JSON.parse(raw);
    if (Date.now() > expiredDate) {
      localStorage.removeItem(`${LS_PREFIX}${type}`);
      return null;
    }
    return items;
  } catch {
    return null;
  }
}

function writeToStorage(type, items, ttlMinutes) {
  try {
    const expiredDate = Date.now() + ttlMinutes * 60 * 1000;
    localStorage.setItem(
      `${LS_PREFIX}${type}`,
      JSON.stringify({ items, expiredDate }),
    );
  } catch {
    // 隐私模式等情况下 localStorage 不可用，静默忽略
  }
}

// ─────────────────────────────────────────────
// 核心：获取某个 type 的枚举列表
// 优先级：内存 → localStorage → API
// ─────────────────────────────────────────────
async function fetchFromApi(type) {
  const res = await getDictItemsByType(type);
  if (res.data.code === 0) {
    const { items, ttl } = res.data.data;
    writeToStorage(type, items, ttl);
    memCache[type] = items;
    return items;
  }
  return [];
}

export async function getDictItems(type) {
  // 1. 命中内存
  if (memCache[type]) return memCache[type];

  // 2. 命中 localStorage
  const cached = readFromStorage(type);
  if (cached) {
    memCache[type] = cached;
    return cached;
  }

  // 3. 有进行中的请求则复用，避免并发重复请求
  if (pendingRequests[type]) return pendingRequests[type];

  // 4. 发起新请求
  pendingRequests[type] = fetchFromApi(type).finally(() => {
    delete pendingRequests[type];
  });

  return pendingRequests[type];
}

// ─────────────────────────────────────────────
// 缓存清除（字典管理页保存后调用）
// ─────────────────────────────────────────────

/** 清除指定 type 的缓存 */
export function clearDictCache(type) {
  delete memCache[type];
  try {
    localStorage.removeItem(`${LS_PREFIX}${type}`);
  } catch {}
}

/** 清除所有字典缓存 */
export function clearAllDictCache() {
  Object.keys(memCache).forEach((k) => delete memCache[k]);
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(LS_PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch {}
}

// ─────────────────────────────────────────────
// React Hooks（薄封装，处理 loading 和渲染触发）
// ─────────────────────────────────────────────

/**
 * 获取某个 type 的枚举列表（只返回启用项 status === 1）
 * const { items, options, loading } = useDictItems('asset_status');
 */
export function useDictItems(type) {
  const [items, setItems] = useState(() => {
    // 初始化时同步读内存缓存，避免首次渲染闪烁
    return memCache[type] || [];
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!type) return;
    // 已有内存缓存则不显示 loading
    if (!memCache[type]) setLoading(true);

    getDictItems(type)
      .then((all) => setItems(all))
      .finally(() => setLoading(false));
  }, [type]);

  const enabledItems = items.filter((i) => i.status === 1);

  return {
    items: enabledItems,
    loading,
    // 直接用于 antd Select options
    options: enabledItems.map((i) => ({
      label: i.label,
      value: i.value,
      extra: i.extra,
    })),
  };
}

/**
 * 获取某个 value 对应的显示 label
 * const label = useDictLabel('asset_status', record.status);
 * const label = useDictLabel('asset_status', record.status, '未知'); // 自定义兜底值
 */
export function useDictLabel(type, value, fallback = value) {
  const [label, setLabel] = useState(() => {
    // 同步读内存缓存
    const cached = memCache[type];
    if (cached) {
      const found = cached.find((i) => i.value === value);
      return found ? found.label : fallback;
    }
    return fallback;
  });

  useEffect(() => {
    if (!type || value == null) return;
    getDictItems(type).then((items) => {
      const found = items.find((i) => i.value === value);
      setLabel(found ? found.label : fallback);
    });
  }, [type, value, fallback]);

  return label;
}

/**
 * 非响应式同步取 label（已确保该 type 已加载时使用，如表格 render 函数内）
 * const label = getDictLabel('asset_status', 'active');
 */
export function getDictLabel(type, value, fallback = value) {
  const items = memCache[type] || [];
  const found = items.find((i) => i.value === value);
  return found ? found.label : fallback;
}
